import { useMemo } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./services.styles";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
} from "recharts";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Generate dynamic colors for services
const generateServiceColors = (services, theme) => {
  const baseColors = [
    theme.palette.primary.main,
    "#8B5CF6",
    "#10B981",
    "#F59E0B",
    "#EC4899",
    "#06B6D4",
    "#84CC16",
    "#F97316",
  ];

  const colors = {};
  services.forEach((service, index) => {
    colors[service] = baseColors[index % baseColors.length];
  });
  return colors;
};

const ServiceBookingsTrend = ({ data: timeSeriesData }) => {
  const theme = useTheme();
  const colors = getColors(theme);

  // Extract data from timeSeriesData
  const labels = timeSeriesData?.labels || [];
  const dataPoints = timeSeriesData?.data || [];
  const allServices = timeSeriesData?.services || [];

  // Generate dynamic colors for all services
  const serviceColors = useMemo(
    () => generateServiceColors(allServices, theme),
    [allServices, theme],
  );

  // Get ALL service keys that actually exist in the data
  const availableServiceKeys = useMemo(() => {
    const keysSet = new Set();
    dataPoints.forEach((point) => {
      Object.keys(point).forEach((key) => {
        if (key !== "label") {
          keysSet.add(key);
        }
      });
    });
    return Array.from(keysSet);
  }, [dataPoints]);

  // Calculate totals for each service and get top services
  const topServices = useMemo(() => {
    const serviceTotals = {};
    dataPoints.forEach((point) => {
      availableServiceKeys.forEach((service) => {
        serviceTotals[service] =
          (serviceTotals[service] || 0) + (point[service] || 0);
      });
    });
    return Object.entries(serviceTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name]) => name);
  }, [dataPoints, availableServiceKeys]);

  // Transform data for chart - only include top services
  const chartData = useMemo(() => {
    return dataPoints.map((point) => {
      const result = { label: point.label || "" };
      topServices.forEach((service) => {
        result[service] = point[service] || 0;
      });
      return result;
    });
  }, [dataPoints, topServices]);

  // Custom legend component
  const CustomLegend = () => (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="flex-end"
      sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
    >
      {topServices.map((service) => (
        <Stack key={service} direction="row" alignItems="center" spacing={0.75}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: 1,
              bgcolor: serviceColors[service] || "#9CA3AF",
            }}
          />
          <Typography
            fontSize={10}
            fontWeight={600}
            color={colors.textSecondary}
            sx={{ textTransform: "uppercase" }}
          >
            {service}
          </Typography>
        </Stack>
      ))}
      {availableServiceKeys.length > 5 && (
        <Tooltip
          title={`Showing top 5 of ${availableServiceKeys.length} services`}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: 12, color: colors.textMuted }} />
            <Typography fontSize={10} color={colors.textMuted}>
              +{availableServiceKeys.length - 5} more
            </Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );

  // Calculate insight
  const insight = useMemo(() => {
    if (chartData.length < 2 || topServices.length === 0) return null;

    const topService = topServices[0];
    const firstPoint = chartData[0][topService] || 0;
    const lastPoint = chartData[chartData.length - 1][topService] || 0;

    if (firstPoint === 0) return null;

    const growth = Math.round(((lastPoint - firstPoint) / firstPoint) * 100);

    return {
      service: topService.charAt(0).toUpperCase() + topService.slice(1),
      growth,
    };
  }, [chartData, topServices]);

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 2.5,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        overflow: "hidden",
        height: 400,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        mt: 4,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={0.5}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          Lead Distribution by Service
        </Typography>
      </Stack>

      <Box sx={{ width: "100%", height: 300, overflow: "hidden" }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            barGap={0}
            barCategoryGap="25%"
            margin={{ top: 5, right: 15, left: -10, bottom: 5 }}
          >
            <CartesianGrid
              vertical={false}
              stroke={colors.borderLight}
              strokeDasharray="4 4"
            />
            <XAxis
              dataKey="label"
              tick={{ fill: colors.textMuted, fontSize: 11, fontWeight: 500 }}
              axisLine={false}
              tickLine={false}
              dy={10}
            />
            <YAxis
              tick={{ fill: colors.textMuted, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              dx={-10}
              domain={[0, "auto"]}
              allowDataOverflow={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "#FFFFFF",
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 8,
                boxShadow: shadows.card,
                fontSize: 12,
                color: colors.textPrimary,
              }}
              itemStyle={{ padding: "2px 0" }}
              labelStyle={{
                fontWeight: 600,
                color: colors.textPrimary,
                marginBottom: 4,
              }}
            />
            <Legend content={<CustomLegend />} />
            {topServices.map((service, index) => (
              <Bar
                key={service}
                dataKey={service}
                name={service.toUpperCase()}
                fill={serviceColors[service]}
                radius={
                  index === topServices.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]
                }
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {insight && insight.growth !== 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${colors.borderLight}`,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 14, color: colors.chartOrange }} />
          <Typography fontSize={11} color={colors.textSecondary}>
            <strong>Insight:</strong> {insight.service} leads{" "}
            <span
              style={{
                color: insight.growth > 0 ? colors.success : colors.danger,
                fontWeight: 600,
              }}
            >
              {insight.growth > 0 ? "increased" : "decreased"} by{" "}
              {Math.abs(insight.growth)}%
            </span>{" "}
            across the selected period
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default ServiceBookingsTrend;
